// same file Vite resolves for pdfme's `import "bwip-js"` (browser build), so the same object
import bwipjs from "bwip-js/browser";

/**
 * pdfme renders every barcode with `textxalign: "center"`, which spreads the digits of EAN / UPC
 * codes evenly under the bars, so the guard bars cut through them (e.g. "0477280652494"). Those
 * symbologies have their own standard text layout (first digit left of the code, two groups of
 * six between the guard bars), which bwip-js draws when textxalign is not set. pdfme uses this
 * same bwip-js object, so its render functions are wrapped here once. Keep in sync with
 * src/pdf/barcodeText.ts (server).
 */
const STANDARD_TEXT = new Set(["ean13", "ean8", "ean5", "ean2", "upca", "upce", "isbn", "ismn", "issn"]);
const MARK = Symbol.for("seedbutler.barcodeText");

type Opts = { bcid?: string; textxalign?: string } & Record<string, unknown>;
const fix = (o: Opts): Opts => {
  if (!o || !STANDARD_TEXT.has(String(o.bcid)) || o.textxalign !== "center") return o;
  const { textxalign: _ignored, ...rest } = o;
  return rest;
};

export function installBarcodeTextFix(lib: Record<string, unknown> = bwipjs as unknown as Record<string, unknown>): void {
  if ((lib as Record<symbol, unknown>)[MARK]) return;
  for (const name of ["toSVG", "toBuffer", "toCanvas", "render"]) {
    const orig = lib[name];
    if (typeof orig !== "function") continue;
    lib[name] = function (this: unknown, ...args: unknown[]) {
      // toSVG/toBuffer(opts), toCanvas(canvas, opts) or toCanvas(opts)
      const i = args.findIndex((a) => a && typeof a === "object" && "bcid" in (a as object));
      if (i >= 0) args[i] = fix(args[i] as Opts);
      return (orig as (...a: unknown[]) => unknown).apply(this, args);
    };
  }
  (lib as Record<symbol, unknown>)[MARK] = true;
}

installBarcodeTextFix();
